import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRoadtripById, addTravel, updateRoadtrip, deleteRoadtrip } from '../services/api';
import Map from '../components/Map';
import EditRoadtripModal from '../components/EditRoadtripModal';
import AddTravelModal from '../components/AddTravelModal';
import ShareRoadtripModal from '../components/ShareRoadtripModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

const RoadtripDetail = () => {
  const { id } = useParams();
  const [roadtrip, setRoadtrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddTravelModal, setShowAddTravelModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isShared, setIsShared] = useState(false);

  useEffect(() => {
    const fetchRoadtrip = async () => {
      try {
        const token = new URLSearchParams(window.location.search).get('token');
        const response = await getRoadtripById(id, token);
        if (response.success) {
          setRoadtrip(response.data);
          setIsShared(!!token);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRoadtrip();
  }, [id]);

  const handleEditSave = async (updatedData) => {
    try {
      // Appel API pour mettre à jour le roadtrip
      // Remplacez par votre fonction d'API de mise à jour
      const response = await updateRoadtrip(id, updatedData); // Assurez-vous d'avoir cette fonction
      if (response.success) {
        setRoadtrip(prev => ({ ...prev, ...updatedData })); // Met à jour l'état local
        setShowEditModal(false);
      } else {
        console.error(response.message);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  const handleAddTravel = async (formData) => {
    try {
      const response = await addTravel(roadtrip.roadtrip_id, formData);
      if (response.success) {
        // Recharger les données du roadtrip pour inclure le nouveau travel
        const updatedRoadtrip = await getRoadtripById(id);
        setRoadtrip(updatedRoadtrip.data);
        setShowAddTravelModal(false);
      } else {
        console.error(response.message);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du travel:', error);
    }
  };

  const handleDeleteRoadtrip = async () => {
    try {
      const response = await deleteRoadtrip(roadtrip.roadtrip_id);
      if (response.success) {
        window.location.href = '/roadtrips';
      } else {
        console.error(response.message);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du roadtrip:', error);
    } finally {
      setShowDeleteModal(false); // Fermer le modal après la suppression
    }
  };

  if (loading) return <div className="spinner-border text-primary" role="status" />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!roadtrip) return <div className="alert alert-warning">Roadtrip non trouvé</div>;

  const getStatusBadge = (status) => {
    switch (status) {
      case "0": return <span className="badge bg-secondary">Brouillon</span>;
      case "1": return <span className="badge bg-success">Terminé</span>;
      case "2": return <span className="badge bg-primary">À venir</span>;
      default: return <span className="badge bg-secondary">Inconnu</span>;
    }
  };

  const renderTravels = () => {
    if (roadtrip.roadtrip_status === "1") {
      // Roadtrip déjà fait : liste simple + bouton ajouter
      return (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Étapes du voyage</h5>
            <div className="list-group">
              {roadtrip.travels.map(travel => (
                <div key={travel.travel_id} className="list-group-item">
                  <h6>{travel.travel_name}</h6>
                  <small className="text-muted">
                    Date: {new Date(travel.travel_date).toLocaleDateString()}
                  </small>
                </div>
              ))}
            </div>
            {!isShared && (
              <button 
                className="btn btn-primary mt-3"
                onClick={() => setShowAddTravelModal(true)}
              >
                <i className="fas fa-plus"></i> Ajouter un GPX
              </button>
            )}
          </div>
        </div>
      );
    } else {
      // Roadtrip à venir : grille de cartes avec mini-maps
      return (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Étapes prévues</h5>
            <div className="row">
              {roadtrip.travels.map(travel => (
                <div key={travel.next_roadtrip_travel_id} className="col-md-4 mb-3">
                  <div className="card h-100">
                    <div style={{ height: '200px' }}>
                      <Map 
                        travels={[travel]} 
                        roadtripStatus={roadtrip.roadtrip_status}
                        miniMap={true}
                      />
                    </div>
                    <div className="card-body">
                      <h6 className="card-title">{travel.next_roadtrip_travel_name}</h6>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Carte "Ajouter" */}
              {!isShared && (
                <div className="col-md-4 mb-3">
                  <div 
                    className="card h-100 d-flex align-items-center justify-content-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowAddTravelModal(true)}
                  >
                    <div className="card-body text-center">
                      <i className="fas fa-plus fa-3x mb-3"></i>
                      <h6>Ajouter une étape</h6>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col">
          <Link to="/roadtrips" className="btn btn-outline-secondary mb-3">
            ← Retour aux roadtrips
          </Link>
          <div className="d-flex justify-content-between align-items-center">
            <h1>{roadtrip.roadtrip_name}</h1>
            {getStatusBadge(roadtrip.roadtrip_status)}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          {roadtrip.lien_externe && roadtrip.lien_externe.includes('.gpx') && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Tracé du parcours</h5>
                <Map gpxUrl={roadtrip.lien_externe} />
              </div>
            </div>
          )}

          {/* Informations principales */}
          <div className="card mb-4">
            <div className="card-body">
              {roadtrip.description && (
                <div className="mb-3">
                  <h5>Description</h5>
                  <p>{roadtrip.description}</p>
                </div>
              )}

              {(roadtrip.date_depart_prevu && roadtrip.date_depart_prevu !== '0000-00-00') && (
                <div className="mb-3">
                  <h5>Dates</h5>
                  <p>
                    Du {new Date(roadtrip.date_depart_prevu).toLocaleDateString()} 
                    {' '}au{' '}
                    {new Date(roadtrip.date_fin_prevu).toLocaleDateString()}
                  </p>
                </div>
              )}

              {roadtrip.lieux && (
                <div className="mb-3">
                  <h5>Lieux</h5>
                  <p>{roadtrip.lieux}</p>
                </div>
              )}

              {roadtrip.infos_supplementaire && (
                <div className="mb-3">
                  <h5>Informations supplémentaires</h5>
                  <p>{roadtrip.infos_supplementaire}</p>
                </div>
              )}

              {roadtrip.lien_externe && (
                <div className="mb-3">
                  <h5>Lien externe</h5>
                  <a 
                    href={roadtrip.lien_externe} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                  >
                    Voir le lien
                  </a>
                </div>
              )}
            </div>
          </div>

          {renderTravels()}

          {roadtrip.images && roadtrip.images.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Photos</h5>
                <div className="row">
                  {roadtrip.images.map((image, index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <img 
                        src={`/images/roadtrips/${image}`} 
                        alt={`Roadtrip ${index + 1}`} 
                        className="img-fluid rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {roadtrip.roadtrip_status === "1" && roadtrip.travels.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Tracé du parcours</h5>
                <Map 
                  travels={roadtrip.travels} 
                  roadtripStatus={roadtrip.roadtrip_status}
                />
              </div>
            </div>
          )}
          
        </div>

        {!isShared && (
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Actions</h5>
                <div className="d-grid gap-2">
                  <button className="btn btn-primary" onClick={() => setShowEditModal(true)}>
                    Modifier
                  </button>
                  <button className="btn btn-outline-danger" onClick={() => setShowDeleteModal(true)}>
                    Supprimer
                  </button>
                  <button 
                    className="btn btn-outline-success" onClick={() => setShowShareModal(true)}>
                    Partager
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {roadtrip && (
        <>
          <EditRoadtripModal
            show={showEditModal}
            handleClose={() => setShowEditModal(false)}
            roadtrip={roadtrip}
            onSave={handleEditSave}
          />
          <AddTravelModal
            show={showAddTravelModal}
            handleClose={() => setShowAddTravelModal(false)}
            roadtripId={roadtrip.roadtrip_id}
            roadtripStatus={roadtrip.roadtrip_status}
            onSave={handleAddTravel}
          />
          <ShareRoadtripModal
            show={showShareModal}
            handleClose={() => setShowShareModal(false)}
            roadtrip={roadtrip}
          />
        </>
      )}

      {/* Modal de confirmation de suppression */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeleteRoadtrip}
      />
    </div>
  );
};

export default RoadtripDetail; 